import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../../services/api";
import AppLayout from "../layout/AppLayout";
import { PieChart, Pie, Cell, ResponsiveContainer } from "recharts";

/* =========================
   COMPONENTES AUXILIARES
========================= */

function Card({ label, value, accent }) {
  const accentMap = {
    blue: "text-blue-400 from-blue-600/20",
    gold: "text-yellow-400 from-yellow-500/20",
    red: "text-red-400 from-red-600/20",
    slate: "text-slate-300 from-slate-600/20"
  };

  return (
    <div className="relative overflow-hidden rounded-2xl border border-[#1f2937] bg-[#0b0f19] p-5">
      <div
        className={`absolute inset-0 bg-gradient-to-br ${accentMap[accent]} to-transparent opacity-60`}
      />
      <div className="relative">
        <p className="text-xs uppercase tracking-wide text-slate-400">
          {label}
        </p>
        <p className={`mt-2 text-4xl font-bold ${accentMap[accent].split(" ")[0]}`}>
          {value}
        </p>
      </div>
    </div>
  );
}

function PeriodFilter({ onApply }) {
  const hoje = new Date().toISOString().slice(0, 10);
  const [inicio, setInicio] = useState(hoje);
  const [fim, setFim] = useState(hoje);

  return (
    <div className="flex flex-wrap items-end gap-3 mb-6">
      <div>
        <label className="text-xs text-slate-400">Início</label>
        <input
          type="date"
          value={inicio}
          onChange={e => setInicio(e.target.value)}
          className="block rounded-lg bg-[#0b0f19] border border-[#1f2937] px-3 py-2 text-sm"
        />
      </div>

      <div>
        <label className="text-xs text-slate-400">Fim</label>
        <input
          type="date"
          value={fim}
          onChange={e => setFim(e.target.value)}
          className="block rounded-lg bg-[#0b0f19] border border-[#1f2937] px-3 py-2 text-sm"
        />
      </div>

      <button
        onClick={() => onApply(inicio, fim)}
        className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold hover:bg-blue-500"
      >
        Filtrar
      </button>
    </div>
  );
}

const COLORS = ["#2563eb", "#facc15", "#64748b", "#dc2626"];

function StatusChart({ data }) {
  if (!data || data.length === 0) {
    return <p className="text-sm text-slate-400">Nenhum dado disponível.</p>;
  }

  return (
    <div className="h-64">
      <ResponsiveContainer width="100%" height="100%">
        <PieChart>
          <Pie data={data} dataKey="total" nameKey="status" outerRadius={90} label>
            {data.map((_, i) => (
              <Cell key={i} fill={COLORS[i % COLORS.length]} />
            ))}
          </Pie>
        </PieChart>
      </ResponsiveContainer>
    </div>
  );
}

/* =========================
   DASHBOARD PRINCIPAL
========================= */

export default function DashboardGeral() {
  const navigate = useNavigate();
  const [data, setData] = useState({
    kpis: { total_processos: 0, ativos: 0, prazos_criticos: 0 },
    prazos: [],
    movimentacoes: [],
    status: []
  });

  const [inicio, setInicio] = useState(null);
  const [fim, setFim] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);

    let url = "/admin_dashboard.php";
    if (inicio && fim) {
      url += `?inicio=${inicio}&fim=${fim}`;
    }

    api
      .get(url)
      .then(res => {
        const apiData = res.data || {};
        setData({
          kpis: apiData.kpis || { total_processos: 0, ativos: 0, prazos_criticos: 0 },
          prazos: apiData.prazos || [],
          movimentacoes: apiData.movimentacoes || [],
          status: apiData.status || []
        });
      })
      .catch(() => {
        setData({
          kpis: { total_processos: 0, ativos: 0, prazos_criticos: 0 },
          prazos: [],
          movimentacoes: [],
          status: []
        });
      })
      .finally(() => setLoading(false));
  }, [inicio, fim]);

  if (loading) {
    return (
      <AppLayout title="Dashboard Geral">
        <p className="text-slate-400">Carregando dados…</p>
      </AppLayout>
    );
  }

  return (
    <AppLayout title="Dashboard Geral" subtitle="Visão executiva do escritório">

      {/* AÇÕES RÁPIDAS */}
      <div className="mb-6 flex flex-col gap-3 sm:flex-row">
        <button
          onClick={() => navigate("/app/admin/prazos/novo")}
          className="rounded-xl bg-green-600 px-4 py-3 text-sm font-semibold hover:bg-green-500"
        >
          + Novo prazo
        </button>

        <button
          onClick={() => navigate("/app/admin/processos/novo")}
          className="rounded-xl bg-indigo-600 px-4 py-3 text-sm font-semibold hover:bg-indigo-500"
        >
          + Novo processo
        </button>
      </div>

      <PeriodFilter onApply={(i, f) => { setInicio(i); setFim(f); }} />

      {/* KPIs */}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Card label="Total de processos" value={data.kpis.total_processos} accent="blue" />
        <Card label="Processos ativos" value={data.kpis.ativos} accent="gold" />
        <Card label="Prazos críticos" value={data.kpis.prazos_criticos} accent="red" />
        <Card label="Atualizações" value={data.movimentacoes.length} accent="slate" />
      </div>

      {/* LISTAS */}
      <div className="mt-8 grid gap-6 md:grid-cols-2">

        {/* PRAZOS */}
        <div className="rounded-xl border border-[#1f2937] bg-[#121826] p-4">
          <h3 className="mb-4 font-semibold text-slate-100">Próximos prazos</h3>

          {data.prazos.length === 0 && (
            <p className="text-sm text-slate-400">Nenhum prazo próximo.</p>
          )}

          <ul className="space-y-3">
            {data.prazos.map((p, i) => (
              <li
                key={i}
                onClick={() => navigate(`/app/admin/processos/${p.processo_id}`)}
                className="cursor-pointer rounded-lg bg-[#0b0f19] p-3 hover:bg-[#1a2233] transition"
              >
                <p className="text-sm text-slate-100">{p.descricao}</p>
                <p className="text-xs text-slate-400">
                  {p.cliente || "Cliente"} • {new Date(p.data_prazo).toLocaleDateString()}
                </p>
              </li>
            ))}
          </ul>

        </div>

        {/* MOVIMENTAÇÕES */}
        <div className="rounded-xl border border-[#1f2937] bg-[#121826] p-4">
          <h3 className="mb-4 font-semibold text-slate-100">Últimas movimentações</h3>

          {data.movimentacoes.length === 0 && (
            <p className="text-sm text-slate-400">Nenhuma movimentação recente.</p>
          )}

          <ul className="space-y-3">
            {data.movimentacoes.map((m, i) => (
              <li
                key={i}
                onClick={() => navigate(`/app/admin/processos/${m.processo_id}`)}
                className="cursor-pointer rounded-lg bg-[#0b0f19] p-3 hover:bg-[#1a2233] transition"
              >
                <p className="text-sm text-slate-100">{m.descricao}</p>
                <p className="text-xs text-slate-400">
                  {m.cliente || "Cliente"} • {new Date(m.criado_em).toLocaleDateString()}
                </p>
              </li>
            ))}
          </ul>

        </div>
      </div>

      {/* GRÁFICO */}
      <div className="mt-8 rounded-xl border border-[#1f2937] bg-[#121826] p-4">
        <h3 className="mb-4 font-semibold text-slate-100">Processos por status</h3>
        <StatusChart data={data.status} />
      </div>

    </AppLayout>
  );
}
